import React, { useCallback } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Link,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import type { PolicySubject } from '../../domain/policies/policyPacks';
import type { PolicyAliasDefinition } from '../../domain/policies/policyPacks';
import { buildReportContextForAlias } from '../../domain/policies/policyPacks';
import type { PolicyCreateData, Policy } from '../../services/policiesApi';
import type { Zone } from '../../services/zonesApi';
import {
  PPE_ITEM_OPTIONS,
  DEFAULT_PPE_REQUIRED_ITEMS,
  SEVERITIES,
  DAYS,
  BEHAVIOR_SENSITIVITY_OPTIONS,
  usesManagedTimingPolicyType,
  getWarehouseEdgeSupport,
  describePolicyUseCase,
  formatPolicySubjectLabel,
  readBehaviorRuleUiState,
  applyBehaviorRuleUiState,
  describeBehaviorPreset,
  readOccupancyRuleUiState,
  applyOccupancyRuleUiState,
  describeOccupancyPreset,
  readWorkerDownRuleUiState,
  applyWorkerDownRuleUiState,
  describeWorkerDownPreset,
  readForkliftProximityRuleUiState,
  applyForkliftProximityRuleUiState,
  describeForkliftProximityPreset,
  readPedestrianNearMovingForkliftRuleUiState,
  applyPedestrianNearMovingForkliftRuleUiState,
  describePedestrianNearMovingForkliftPreset,
  readForkliftSpeedingRuleUiState,
  applyForkliftSpeedingRuleUiState,
  describeForkliftSpeedingPreset,
  readZoneIntrusionRuleUiState,
  applyZoneIntrusionRuleUiState,
  describeZoneIntrusionPreset,
  readLineCrossingRuleUiState,
  applyLineCrossingRuleUiState,
  describeLineCrossingPreset,
  readRegionTransitionRuleUiState,
  applyRegionTransitionRuleUiState,
  describeRegionTransitionPreset,
  getPolicyActionByType,
  stripOutboundPolicyActions,
  otherOutboundThan,
  truthyParamFlag,
  canonicalPatternPayload,
  immediateAlertEnabled,
  canonicalAlertForSave,
  effectiveAlertLinesFromForm,
  FORKLIFT_PATTERN_COMMISSIONING_COPY,
  PATTERN_ALERT_POLICY_TYPES,
  type BehaviorMode,
  type BehaviorSensitivity,
  type OccupancyMode,
  type WorkerDownMode,
  type ForkliftProximityMode,
  type PedestrianNearMovingForkliftMode,
  type ForkliftSpeedingMode,
  type ZoneIntrusionMode,
  type LineCrossingMode,
  type RegionTransitionMode,
} from './policyConfigPresets';

export interface PolicyConfigureStepProps {
  selectedPolicyOption: PolicyAliasDefinition | undefined;
  selectedPolicySubject: PolicySubject;
  formData: Partial<PolicyCreateData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<PolicyCreateData>>>;

  selectedCameraId: string;
  selectedCameraProfileId?: 'A' | 'B' | 'C' | 'D' | '';
  setSelectedCameraId: (id: string) => void;
  selectedEdgeDeviceId: string;
  setSelectedEdgeDeviceId: (id: string) => void;
  cameras: Array<{ camera_id: string; edge_device_id?: string; name?: string; [k: string]: any }>;

  selectedZoneIds: string[];
  setSelectedZoneIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedLineId: string;
  setSelectedLineId: (id: string) => void;
  lineDirection: 'both' | 'a_to_b' | 'b_to_a';
  setLineDirection: (dir: 'both' | 'a_to_b' | 'b_to_a') => void;
  availableZones: Zone[];
  availableLines: any[];
  openZonePreview: (zone: Zone) => void;

  scopeReady: boolean;
  scopeRequirements: string[];
  editorTab: 'scope' | 'hours' | 'actions';
  setEditorTab: (tab: 'scope' | 'hours' | 'actions') => void;
  weekGrid: Record<string, boolean[]>;
  setWeekGrid: React.Dispatch<React.SetStateAction<Record<string, boolean[]>>>;
  initDefaultGrid: () => Record<string, boolean[]>;
  gridToWindows: (g: Record<string, boolean[]>) => any;
  isTimeBasedSchedule: (schedule: any) => boolean;

  mode: string;
  saving: boolean;
  editingPolicy: any | null;
  clockStatusByDevice: Record<string, boolean | null>;
  deriveDeviceIdsForPolicy: (
    policy: Partial<PolicyCreateData> | Policy,
    opts?: { cameraId?: string; edgeDeviceId?: string; zoneIds?: string[]; lineId?: string },
  ) => string[];
  navigate: (path: string) => void;
  setSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' }) => void;
}

const PolicyConfigureStep: React.FC<PolicyConfigureStepProps> = ({
  selectedPolicyOption,
  selectedPolicySubject,
  formData,
  setFormData,
  selectedCameraId,
  selectedCameraProfileId,
  setSelectedCameraId,
  selectedEdgeDeviceId,
  setSelectedEdgeDeviceId,
  cameras,
  selectedZoneIds,
  setSelectedZoneIds,
  selectedLineId,
  setSelectedLineId,
  lineDirection,
  setLineDirection,
  availableZones,
  availableLines,
  openZonePreview,
  scopeReady,
  scopeRequirements,
  editorTab,
  setEditorTab,
  weekGrid,
  setWeekGrid,
  initDefaultGrid,
  gridToWindows,
  isTimeBasedSchedule: isTimeBased,
  mode,
  saving: _saving,
  editingPolicy,
  clockStatusByDevice,
  deriveDeviceIdsForPolicy,
  navigate,
  setSnackbar,
}) => {
  const configuredPolicyAlias = String(selectedPolicyOption?.alias || '').trim();
  const configuredPolicyContext = configuredPolicyAlias
    ? buildReportContextForAlias(configuredPolicyAlias)
    : null;
  const formatContextToken = (value?: string | null) =>
    String(value || '')
      .trim()
      .replace(/[_-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const setTimingNumber = useCallback(
    (key: 'cooldown_seconds' | 'persistence_seconds' | 'min_dwell_seconds' | 'dedup_window_seconds', raw: string) => {
      setFormData((prev) => {
        const timing: any = { ...((prev as any).timing || {}) };
        if (raw === '') {
          delete timing[key];
        } else {
          const v = Number(raw);
          if (!Number.isFinite(v)) return prev as any;
          timing[key] = v;
        }
        const nextTiming = Object.keys(timing).length ? timing : undefined;
        return { ...prev, timing: nextTiming } as any;
      });
    },
    [setFormData],
  );

  const handleZoneToggle = useCallback(
    (zoneId: string) => {
      if (!selectedCameraId) {
        setSnackbar({ open: true, message: 'Select a camera first (v1 rule).', severity: 'error' });
        return;
      }
      setSelectedZoneIds((prev) => (prev.includes(zoneId) ? prev.filter((id) => id !== zoneId) : [...prev, zoneId]));
    },
    [selectedCameraId, setSelectedZoneIds, setSnackbar],
  );

  return (
    <>
      {/* Configure header */}
      <Alert severity={scopeReady ? 'success' : 'warning'} sx={{ mb: 2 }}>
        {scopeReady
          ? `Ready for review: ${selectedPolicyOption?.label || 'Policy'} is scoped to the selected geometry.`
          : `Next required: ${scopeRequirements.join(' · ')}.`}
      </Alert>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Chip size="small" label={`Policy: ${selectedPolicyOption?.label || 'Zone Intrusion'}`} color="primary" variant="outlined" />
        <Chip size="small" label={`Subject: ${formatPolicySubjectLabel(selectedPolicySubject)}`} />
        <Chip size="small" label={`Engine: ${selectedPolicyOption?.label || String(formData.policy_type || 'zone_intrusion')}`} />
      </Box>

      {configuredPolicyAlias ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Current scan meaning
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {configuredPolicyContext?.hazard_exposure_label || selectedPolicyOption?.label || 'Configured safety rule'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {`Alias: ${configuredPolicyAlias} · Scan template: ${formatContextToken(configuredPolicyContext?.scan_template)} · Exposure category: ${formatContextToken(configuredPolicyContext?.exposure_category)}`}
          </Typography>
          {configuredPolicyContext?.claims_roi_alignment?.commercial_priority ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {`Claims / ROI alignment: ${formatContextToken(configuredPolicyContext.claims_roi_alignment.claims_alignment_category)} · Commercial priority: ${formatContextToken(configuredPolicyContext.claims_roi_alignment.commercial_priority)}`}
            </Typography>
          ) : null}
          {configuredPolicyContext?.safety_control_review?.primary_control_lens ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {`Safety control review: ${formatContextToken(configuredPolicyContext.safety_control_review.primary_control_lens)} · Verification: ${formatContextToken(configuredPolicyContext.safety_control_review.verification_status)}`}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Use Back to change the rule meaning before you save if this scope should map to a different buyer-facing scan.
          </Typography>
        </Alert>
      ) : null}

      {mode !== 'ocr' && (() => {
        const meta = getWarehouseEdgeSupport(
          String(formData.policy_type || 'zone_intrusion'),
          selectedCameraProfileId,
        );
        if (!meta.message && meta.support === 'supported') return null;
        const sev =
          meta.support === 'not_supported'
            ? 'error'
            : meta.support === 'supported'
              ? 'info'
              : 'warning';
        return (
          <Alert severity={sev as any} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Edge execution note (Warehouse mode)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {meta.message || 'This policy may not produce incidents on the edge yet.'} See <code>docs/POLICY_TYPE_COVERAGE.md</code>.
            </Typography>
          </Alert>
        );
      })()}

      <Tabs value={editorTab} onChange={(_, v) => setEditorTab(v)} sx={{ mb: 2 }} variant="fullWidth">
        <Tab value="scope" label="Scope & Geometry" />
        <Tab value="hours" label="Active Hours" />
        <Tab value="actions" label="Actions & Notifications" />
      </Tabs>

      {/* Scope tab */}
      {editorTab === 'scope' && (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="policy-camera-label">Camera</InputLabel>
            <Select
              labelId="policy-camera-label"
              value={selectedCameraId ? `${selectedEdgeDeviceId || ''}::${selectedCameraId}` : ''}
              label="Camera"
              onChange={(e) => {
                const raw = String(e.target.value || '').trim();
                if (!raw) {
                  setSelectedCameraId('');
                  setSelectedEdgeDeviceId('');
                  return;
                }
                const parts = raw.split('::');
                const dev = String(parts[0] || '').trim();
                const camId = String(parts.slice(1).join('::') || '').trim();
                setSelectedEdgeDeviceId(dev);
                setSelectedCameraId(camId);
              }}
              renderValue={(val) => {
                const raw = String(val || '').trim();
                const parts = raw.split('::');
                const dev = String(parts[0] || '').trim();
                const cid = String(parts.slice(1).join('::') || '').trim();
                const cam = cameras.find((c) => String(c.camera_id) === cid && (!dev || String(c.edge_device_id) === dev));
                if (!cam) return cid || 'Select camera…';
                const nm = String(cam.name || '').trim();
                const id = String(cam.camera_id || '').trim();
                const devShort = String(cam.edge_device_id || '').trim();
                const base = nm && nm !== id ? `${nm} (${id})` : id;
                return devShort ? `${base} — ${devShort}` : base;
              }}
            >
              <MenuItem value="" disabled>
                Select camera…
              </MenuItem>
              {cameras.length === 0 ? (
                <MenuItem value="__none__" disabled>
                  No cameras loaded — close and refresh Policies
                </MenuItem>
              ) : (
                cameras.map((cam) => (
                  <MenuItem key={`${cam.edge_device_id}:${cam.camera_id}`} value={`${String(cam.edge_device_id)}::${String(cam.camera_id)}`}>
                    {(() => {
                      const nm = String(cam.name || '').trim();
                      const id = String(cam.camera_id || '').trim();
                      const devShort = String(cam.edge_device_id || '').trim();
                      const base = nm && nm !== id ? `${nm} (${id})` : id;
                      return devShort ? `${base} — ${devShort}` : base;
                    })()}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {cameras.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Camera list failed to load (or timed out). Refresh the Policies page, then reopen Create Policy.
            </Alert>
          )}

          {(() => {
            const useCaseSummary = describePolicyUseCase(selectedPolicyOption?.alias, String(formData.policy_type || ''));
            if (!useCaseSummary) return null;
            return (
              <Alert severity="info" sx={{ mb: 2 }}>
                {useCaseSummary}
              </Alert>
            );
          })()}

          {formData.policy_type === 'ppe_compliance' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Required PPE Items</InputLabel>
              <Select
                multiple
                label="Required PPE Items"
                value={
                  (Array.isArray((formData.config as any)?.required_items)
                    ? ((formData.config as any)?.required_items as any[]).map((x) => String(x))
                    : DEFAULT_PPE_REQUIRED_ITEMS) as any
                }
                onChange={(e) => {
                  const v = e.target.value as any;
                  const items = (Array.isArray(v) ? v : [v]).map((x: any) => String(x));
                  setFormData({
                    ...formData,
                    config: {
                      ...(formData.config || {}),
                      required_items: items,
                    },
                  });
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as any[]).map((val) => (
                      <Chip key={String(val)} label={String(val)} size="small" />
                    ))}
                  </Box>
                )}
              >
                {PPE_ITEM_OPTIONS.map((opt) => {
                  const current = Array.isArray((formData.config as any)?.required_items)
                    ? ((formData.config as any)?.required_items as any[]).map((x) => String(x))
                    : DEFAULT_PPE_REQUIRED_ITEMS;
                  const checked = current.includes(opt.value);
                  return (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={checked} />
                      <ListItemText primary={opt.label} secondary={opt.value} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}

          {formData.policy_type === 'line_crossing' ? (
            (() => {
              const crossingUi = readLineCrossingRuleUiState((formData.config as any) || {});
              const presetSummary = describeLineCrossingPreset(crossingUi.sensitivity);
              return (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Line Crossing Configuration
                  </Typography>

                  <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                    <FormControl sx={{ minWidth: 220, flex: 1 }}>
                      <InputLabel>Mode</InputLabel>
                      <Select
                        value={crossingUi.mode}
                        label="Mode"
                        onChange={(e) => {
                          const m = e.target.value as LineCrossingMode;
                          setFormData((prev) => ({
                            ...prev,
                            config: applyLineCrossingRuleUiState((prev.config as any) || {}, {
                              ...readLineCrossingRuleUiState((prev.config as any) || {}),
                              mode: m,
                            }),
                          }));
                        }}
                      >
                        <MenuItem value="off">Off</MenuItem>
                        <MenuItem value="crossing">Crossing</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ minWidth: 220, flex: 1 }}>
                      <InputLabel>Sensitivity</InputLabel>
                      <Select
                        value={crossingUi.sensitivity}
                        label="Sensitivity"
                        onChange={(e) => {
                          const sensitivity = e.target.value as BehaviorSensitivity;
                          setFormData((prev) => ({
                            ...prev,
                            config: applyLineCrossingRuleUiState((prev.config as any) || {}, {
                              ...readLineCrossingRuleUiState((prev.config as any) || {}),
                              sensitivity,
                            }),
                          }));
                        }}
                      >
                        {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label} — {opt.helper}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select Line</InputLabel>
                    <Select
                      value={selectedLineId}
                      label="Select Line"
                      onChange={(e) => setSelectedLineId(e.target.value)}
                      disabled={!selectedCameraId}
                    >
                      {availableLines.length === 0 ? (
                        <MenuItem value="" disabled>
                          {selectedCameraId ? 'No enabled lines for this camera' : 'Select a camera first'}
                        </MenuItem>
                      ) : (
                        availableLines.map((l) => (
                          <MenuItem key={l.line_id} value={l.line_id}>
                            {l.name} ({l.line_id})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Direction</InputLabel>
                    <Select value={lineDirection} label="Direction" onChange={(e) => setLineDirection(e.target.value as any)}>
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="a_to_b">A → B</MenuItem>
                      <MenuItem value="b_to_a">B → A</MenuItem>
                    </Select>
                  </FormControl>

                  <Alert severity="info" sx={{ mb: 2 }}>
                    {presetSummary} This policy runs on the edge using the line geometry you drew.
                  </Alert>
                </>
              );
            })()
          ) : (
            <>
              {(formData.policy_type === 'ppe_compliance' || formData.policy_type === 'occupancy' || formData.policy_type === 'worker_down') && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedZoneIds.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedZoneIds([]);
                          setFormData((prev) => ({
                            ...prev,
                            scope: { ...(prev.scope || {}), zone_ids: [] },
                          }));
                        }
                      }}
                    />
                  }
                  label="Apply to whole camera view (no zones)"
                />
              )}

              <Typography variant="subtitle2" gutterBottom>
                Select Zones ({selectedZoneIds.length} selected)
              </Typography>
              {availableZones.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {selectedCameraId
                    ? 'No enabled zones found for this camera. Create zones in the Zones page.'
                    : 'No enabled zones found. Create zones in the Zones page.'}
                </Alert>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', mb: 2 }}>
                    <List dense>
                      {availableZones.map((zone) => (
                        <ListItem key={zone.zone_id} button onClick={() => handleZoneToggle(zone.zone_id)}>
                          <Checkbox checked={selectedZoneIds.includes(zone.zone_id)} />
                          <ListItemText
                            primary={zone.name || zone.zone_id}
                            secondary={`${zone.zone_id}${zone.camera_id ? ` • Camera: ${zone.camera_id}` : ''}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                openZonePreview(zone);
                              }}
                              title="Preview zone on camera thumbnail"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                  {editingPolicy && editingPolicy.scope?.zone_ids && editingPolicy.scope.zone_ids.length > 0 && (() => {
                    const zones = availableZones;
                    const allZones = zones; // availableZones already filtered to this camera
                    const missingZones = editingPolicy.scope.zone_ids.filter((id: string) => !allZones.some((z: Zone) => z.zone_id === id));
                    const disabledZones = editingPolicy.scope.zone_ids.filter((id: string) => allZones.some((z: Zone) => z.zone_id === id && z.enabled === false));
                    if (missingZones.length > 0 || disabledZones.length > 0) {
                      return (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Zone Warnings:
                          </Typography>
                          {missingZones.length > 0 && (
                            <Typography variant="body2">Missing zones: {missingZones.join(', ')}. These zones may have been deleted.</Typography>
                          )}
                          {disabledZones.length > 0 && (
                            <Typography variant="body2">Disabled zones: {disabledZones.join(', ')}. These zones are currently disabled.</Typography>
                          )}
                        </Alert>
                      );
                    }
                    return null;
                  })()}
                </>
              )}

              {formData.policy_type === 'forklift_proximity' && (
                (() => {
                  const forkliftUi = readForkliftProximityRuleUiState((formData.config as any) || {});
                  const presetSummary = describeForkliftProximityPreset(forkliftUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Unsafe Forklift-Pedestrian Proximity Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={forkliftUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as ForkliftProximityMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyForkliftProximityRuleUiState((prev.config as any) || {}, {
                                  ...readForkliftProximityRuleUiState((prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="proximity">Proximity</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={forkliftUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyForkliftProximityRuleUiState((prev.config as any) || {}, {
                                  ...readForkliftProximityRuleUiState((prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary} Selected zones only decide where this rule applies.
                      </Alert>
                    </>
                  );
                })()
              )}

              {formData.policy_type === 'worker_down' && (
                (() => {
                  const workerDownUi = readWorkerDownRuleUiState((formData.config as any) || {});
                  const presetSummary = describeWorkerDownPreset(workerDownUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Worker Down Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={workerDownUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as WorkerDownMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyWorkerDownRuleUiState((prev.config as any) || {}, {
                                  ...readWorkerDownRuleUiState((prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="worker_down">Worker Down</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={workerDownUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyWorkerDownRuleUiState((prev.config as any) || {}, {
                                  ...readWorkerDownRuleUiState((prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary} This staged policy is intended for Worker Protection cameras and still depends on runtime, evidence, and validation gates before operational rollout.
                      </Alert>
                    </>
                  );
                })()
              )}

              {formData.policy_type === 'pedestrian_near_moving_forklift' && (
                (() => {
                  const movingUi = readPedestrianNearMovingForkliftRuleUiState((formData.config as any) || {});
                  const presetSummary = describePedestrianNearMovingForkliftPreset(movingUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Pedestrian Near Moving Forklift Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={movingUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as PedestrianNearMovingForkliftMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyPedestrianNearMovingForkliftRuleUiState((prev.config as any) || {}, {
                                  ...readPedestrianNearMovingForkliftRuleUiState((prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="near_moving">Near Moving</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={movingUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyPedestrianNearMovingForkliftRuleUiState((prev.config as any) || {}, {
                                  ...readPedestrianNearMovingForkliftRuleUiState((prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary} Selected zones only decide where this rule applies.
                      </Alert>
                    </>
                  );
                })()
              )}

              {formData.policy_type === 'forklift_speeding' && (
                (() => {
                  const speedingUi = readForkliftSpeedingRuleUiState((formData.config as any) || {});
                  const presetSummary = describeForkliftSpeedingPreset(speedingUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Forklift Speeding Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={speedingUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as ForkliftSpeedingMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyForkliftSpeedingRuleUiState((prev.config as any) || {}, {
                                  ...readForkliftSpeedingRuleUiState((prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="speeding">Speeding</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={speedingUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyForkliftSpeedingRuleUiState((prev.config as any) || {}, {
                                  ...readForkliftSpeedingRuleUiState((prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary} Selected zones only decide where this rule applies.
                      </Alert>
                    </>
                  );
                })()
              )}

              {formData.policy_type === 'zone_intrusion' && (
                (() => {
                  const intrusionUi = readZoneIntrusionRuleUiState((formData.config as any) || {});
                  const presetSummary = describeZoneIntrusionPreset(intrusionUi.mode, intrusionUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Zone Intrusion Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={intrusionUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as ZoneIntrusionMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyZoneIntrusionRuleUiState((prev.config as any) || {}, {
                                  ...readZoneIntrusionRuleUiState((prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="restricted">Restricted</MenuItem>
                            <MenuItem value="warning">Warning</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={intrusionUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyZoneIntrusionRuleUiState((prev.config as any) || {}, {
                                  ...readZoneIntrusionRuleUiState((prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary}
                      </Alert>
                    </>
                  );
                })()
              )}

              {formData.policy_type === 'dwell_violation' && (
                (() => {
                  const behaviorUi = readBehaviorRuleUiState('dwell_violation', (formData.config as any) || {});
                  const presetSummary = describeBehaviorPreset('dwell_violation', behaviorUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Dwell Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={behaviorUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as BehaviorMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyBehaviorRuleUiState('dwell_violation', (prev.config as any) || {}, {
                                  ...readBehaviorRuleUiState('dwell_violation', (prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="dwell">Dwell</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={behaviorUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyBehaviorRuleUiState('dwell_violation', (prev.config as any) || {}, {
                                  ...readBehaviorRuleUiState('dwell_violation', (prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Threshold Override (seconds)"
                          type="number"
                          value={behaviorUi.thresholdOverrideSeconds}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw !== '' && !Number.isFinite(Number(raw))) return;
                            setFormData((prev) => ({
                              ...prev,
                              config: applyBehaviorRuleUiState('dwell_violation', (prev.config as any) || {}, {
                                ...readBehaviorRuleUiState('dwell_violation', (prev.config as any) || {}),
                                thresholdOverrideSeconds: raw,
                              }),
                            }));
                          }}
                          helperText="Optional. Overrides only the threshold. Presets still drive persistence and cooldown."
                          sx={{ minWidth: 260, flex: 1 }}
                        />
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary} Use sensitivity for normal tuning and only set a threshold override when you need a specific dwell window.
                      </Alert>
                    </>
                  );
                })()
              )}

              {formData.policy_type === 'loitering' && (
                (() => {
                  const behaviorUi = readBehaviorRuleUiState('loitering', (formData.config as any) || {});
                  const presetSummary = describeBehaviorPreset('loitering', behaviorUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Loitering Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={behaviorUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as BehaviorMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyBehaviorRuleUiState('loitering', (prev.config as any) || {}, {
                                  ...readBehaviorRuleUiState('loitering', (prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="loiter">Loiter</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={behaviorUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyBehaviorRuleUiState('loitering', (prev.config as any) || {}, {
                                  ...readBehaviorRuleUiState('loitering', (prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Threshold Override (seconds)"
                          type="number"
                          value={behaviorUi.thresholdOverrideSeconds}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw !== '' && !Number.isFinite(Number(raw))) return;
                            setFormData((prev) => ({
                              ...prev,
                              config: applyBehaviorRuleUiState('loitering', (prev.config as any) || {}, {
                                ...readBehaviorRuleUiState('loitering', (prev.config as any) || {}),
                                thresholdOverrideSeconds: raw,
                              }),
                            }));
                          }}
                          helperText="Optional. Overrides only the threshold. Presets still drive movement tolerance, persistence, and cooldown."
                          sx={{ minWidth: 260, flex: 1 }}
                        />
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary} Use sensitivity for normal tuning and only set a threshold override when you need a specific linger window.
                      </Alert>
                    </>
                  );
                })()
              )}

              {!usesManagedTimingPolicyType(String(formData.policy_type || '')) && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Timing (noise control)
                  </Typography>
                  <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                    <TextField
                      label="Cooldown (seconds)"
                      type="number"
                      value={(formData as any)?.timing?.cooldown_seconds ?? ''}
                      onChange={(e) => setTimingNumber('cooldown_seconds', e.target.value)}
                      helperText="Minimum time between repeated incidents for this policy."
                      sx={{ minWidth: 240, flex: 1 }}
                    />
                    <TextField
                      label="Min dwell (seconds)"
                      type="number"
                      value={(formData as any)?.timing?.min_dwell_seconds ?? ''}
                      onChange={(e) => setTimingNumber('min_dwell_seconds', e.target.value)}
                      helperText="Require condition to hold for this long before firing (when applicable)."
                      sx={{ minWidth: 240, flex: 1 }}
                    />
                    <TextField
                      label="Persistence (seconds)"
                      type="number"
                      value={(formData as any)?.timing?.persistence_seconds ?? ''}
                      onChange={(e) => setTimingNumber('persistence_seconds', e.target.value)}
                      helperText="Require stable tracking for this long before firing (when applicable)."
                      sx={{ minWidth: 240, flex: 1 }}
                    />
                    <TextField
                      label="Dedup window (seconds)"
                      type="number"
                      value={(formData as any)?.timing?.dedup_window_seconds ?? ''}
                      onChange={(e) => setTimingNumber('dedup_window_seconds', e.target.value)}
                      helperText="Suppress repeat incidents for same person/zone within this window. E.g. 600 = one incident per 10 min."
                      sx={{ minWidth: 240, flex: 1 }}
                    />
                  </Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Leave blank to use defaults. Note: zone-level timing (Zones YAML) can also apply as a fallback for zone-based alerts.
                  </Alert>
                </>
              )}

              {formData.policy_type === 'occupancy' && (
                (() => {
                  const occupancyUi = readOccupancyRuleUiState((formData.config as any) || {});
                  const presetSummary = describeOccupancyPreset(occupancyUi.sensitivity);
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Occupancy Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={occupancyUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as OccupancyMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyOccupancyRuleUiState((prev.config as any) || {}, {
                                  ...readOccupancyRuleUiState((prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value="occupancy">Occupancy</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label="Max Occupancy (people)"
                          type="number"
                          value={occupancyUi.maxOccupancy}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw !== '' && !Number.isFinite(Number(raw))) return;
                            setFormData((prev) => ({
                              ...prev,
                              config: applyOccupancyRuleUiState((prev.config as any) || {}, {
                                ...readOccupancyRuleUiState((prev.config as any) || {}),
                                maxOccupancy: raw,
                              }),
                            }));
                          }}
                          helperText="Triggers when people in zone > this value. Set 0 to alert when 1+ person is present."
                          sx={{ minWidth: 240, flex: 1 }}
                        />
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={occupancyUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyOccupancyRuleUiState((prev.config as any) || {}, {
                                  ...readOccupancyRuleUiState((prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary}
                      </Alert>
                    </>
                  );
                })()
              )}

              {(formData.policy_type === 'region_entrance' || formData.policy_type === 'region_exiting') && (
                (() => {
                  const regionUi = readRegionTransitionRuleUiState(String(formData.policy_type || ''), (formData.config as any) || {});
                  const presetSummary = describeRegionTransitionPreset(regionUi.sensitivity);
                  const isExit = formData.policy_type === 'region_exiting';
                  return (
                    <>
                      <Typography variant="subtitle2" gutterBottom>
                        Region Transition Behavior
                      </Typography>
                      <Box display="flex" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Mode</InputLabel>
                          <Select
                            value={regionUi.mode}
                            label="Mode"
                            onChange={(e) => {
                              const m = e.target.value as RegionTransitionMode;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyRegionTransitionRuleUiState((prev.config as any) || {}, {
                                  ...readRegionTransitionRuleUiState(String(prev.policy_type || ''), (prev.config as any) || {}),
                                  mode: m,
                                }),
                              }));
                            }}
                          >
                            <MenuItem value="off">Off</MenuItem>
                            <MenuItem value={isExit ? 'exiting' : 'entrance'}>{isExit ? 'Exiting' : 'Entrance'}</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl sx={{ minWidth: 220, flex: 1 }}>
                          <InputLabel>Sensitivity</InputLabel>
                          <Select
                            value={regionUi.sensitivity}
                            label="Sensitivity"
                            onChange={(e) => {
                              const sensitivity = e.target.value as BehaviorSensitivity;
                              setFormData((prev) => ({
                                ...prev,
                                config: applyRegionTransitionRuleUiState((prev.config as any) || {}, {
                                  ...readRegionTransitionRuleUiState(String(prev.policy_type || ''), (prev.config as any) || {}),
                                  sensitivity,
                                }),
                              }));
                            }}
                          >
                            {BEHAVIOR_SENSITIVITY_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} — {opt.helper}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {presetSummary}
                      </Alert>
                    </>
                  );
                })()
              )}
            </>
          )}
        </>
      )}

      {/* Hours tab */}
      {editorTab === 'hours' && (
        <>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!(formData.schedule as any)?.enabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormData({
                    ...formData,
                    schedule: enabled
                      ? {
                          enabled: true,
                          timezone: (formData.schedule as any)?.timezone || 'UTC',
                          windows: gridToWindows(Object.keys(weekGrid).length ? weekGrid : initDefaultGrid()),
                          exceptions: (formData.schedule as any)?.exceptions || [],
                        }
                      : undefined,
                  });
                }}
              />
            }
            label="Enable Active Hours (otherwise always active)"
          />

          {(formData.schedule as any)?.enabled === true && (
            <>
              {(() => {
                if (!isTimeBased(formData.schedule)) return null;
                const deviceIds = deriveDeviceIdsForPolicy(formData, {
                  cameraId: selectedCameraId,
                  zoneIds: selectedZoneIds,
                  lineId: selectedLineId,
                });
                const unsynced = deviceIds.filter((id) => clockStatusByDevice[id] === true);
                if (unsynced.length === 0) return null;
                const preflightTarget = unsynced[0] || '';
                return (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Clock unsynced: schedules may not run correctly until the edge device time is synchronized.{' '}
                    <Link
                      component="button"
                      onClick={() => navigate(preflightTarget ? `/preflight?device=${encodeURIComponent(preflightTarget)}` : '/preflight')}
                    >
                      Open Preflight (Time Check)
                    </Link>
                    .
                  </Alert>
                );
              })()}
              <TextField
                fullWidth
                label="Timezone"
                value={(formData.schedule as any)?.timezone || 'UTC'}
                onChange={(e) => setFormData({ ...formData, schedule: { ...(formData.schedule as any), timezone: e.target.value } })}
                sx={{ mt: 2 }}
                helperText="IANA timezone, e.g. UTC, America/New_York"
              />
              <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '64px repeat(24, 24px)', alignItems: 'center' }}>
                  <Box />
                  {Array.from({ length: 24 }, (_, h) => (
                    <Box key={h} sx={{ fontSize: 10, color: 'text.secondary', textAlign: 'center', py: 0.5 }}>
                      {h}
                    </Box>
                  ))}
                  {DAYS.map((d) => (
                    <React.Fragment key={d.key}>
                      <Box sx={{ fontSize: 12, px: 1, py: 1, color: 'text.secondary' }}>{d.label}</Box>
                      {Array.from({ length: 24 }, (_, h) => {
                        const on = !!(weekGrid?.[d.key]?.[h]);
                        return (
                          <Box
                            key={`${d.key}-${h}`}
                            onClick={() => {
                              const next = { ...(weekGrid || {}) };
                              const row = Array.isArray(next[d.key]) ? [...next[d.key]] : Array.from({ length: 24 }, () => false);
                              row[h] = !row[h];
                              next[d.key] = row;
                              setWeekGrid(next);
                              setFormData({
                                ...formData,
                                schedule: {
                                  ...(formData.schedule as any),
                                  enabled: true,
                                  timezone: (formData.schedule as any)?.timezone || 'UTC',
                                  windows: gridToWindows(next),
                                },
                              });
                            }}
                            sx={{
                              width: 24,
                              height: 24,
                              cursor: 'pointer',
                              borderTop: '1px solid',
                              borderColor: 'divider',
                              backgroundColor: on ? 'primary.main' : 'transparent',
                            }}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
              <Alert severity="info" sx={{ mt: 2 }}>
                Weekly calendar: click hour blocks to enable/disable.
              </Alert>
            </>
          )}
        </>
      )}

      {/* Actions tab */}
      {editorTab === 'actions' && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Delivery targets
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Outbound email/SMS use server configuration (SMTP_HOST, TWILIO_*, etc.). This form only stores
            recipients, subjects, and URLs. Genetec uses HTTPS POST to your endpoint (same allowlist rules as
            webhooks).
          </Alert>

          <FormControlLabel
            control={
              <Checkbox
                checked={Array.isArray((formData as any).actions) ? ((formData as any).actions || []).some((a: any) => a?.type === 'odoo_ticket' && a?.enabled !== false) : false}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  const prev = Array.isArray((formData as any).actions) ? ([...(formData as any).actions] as any[]) : [];
                  const next = prev.filter((a) => a?.type !== 'odoo_ticket');
                  if (enabled) next.push({ type: 'odoo_ticket', enabled: true, on: 'incident.created' });
                  setFormData({ ...formData, actions: next.length ? next : [] } as any);
                }}
              />
            }
            label="Create Odoo ticket (on incident.created)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(getPolicyActionByType((formData as any).actions, 'email.notify'))}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData((prev) => {
                    const base = stripOutboundPolicyActions(prev.actions);
                    const rest = otherOutboundThan(prev.actions, 'email.notify');
                    if (!on) {
                      const merged = [...base, ...rest];
                      return { ...prev, actions: merged.length ? merged : undefined } as any;
                    }
                    const existing = getPolicyActionByType(prev.actions, 'email.notify');
                    const addition = {
                      id: existing?.id || 'email_notify',
                      type: 'email.notify',
                      enabled: true,
                      plane: 'cloud',
                      params: {
                        to: String(existing?.params?.to ?? ''),
                        subject: String(existing?.params?.subject ?? 'Safety incident notification'),
                        body: String(existing?.params?.body ?? ''),
                      },
                    };
                    return { ...prev, actions: [...base, ...rest, addition] } as any;
                  });
                }}
              />
            }
            label="Email (on incident.created)"
          />
          {getPolicyActionByType((formData as any).actions, 'email.notify') ? (
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="To"
                placeholder="ops@example.com, supervisor@example.com"
                value={String(getPolicyActionByType((formData as any).actions, 'email.notify')?.params?.to ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'email.notify' ? { ...a, params: { ...a.params, to: v } } : a,
                    ),
                  }));
                }}
                helperText="Comma-separated addresses."
              />
              <TextField
                fullWidth
                size="small"
                label="Subject"
                value={String(getPolicyActionByType((formData as any).actions, 'email.notify')?.params?.subject ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'email.notify' ? { ...a, params: { ...a.params, subject: v } } : a,
                    ),
                  }));
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Body (optional)"
                multiline
                minRows={2}
                value={String(getPolicyActionByType((formData as any).actions, 'email.notify')?.params?.body ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'email.notify' ? { ...a, params: { ...a.params, body: v } } : a,
                    ),
                  }));
                }}
                helperText="Plain text. If empty, the server sends a full incident summary; if you type a message, that text is kept and the summary is appended. A snapshot image is attached when the file is on the cloud (EMAIL_NOTIFY_ATTACH_SNAPSHOT)."
              />
            </Box>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(getPolicyActionByType((formData as any).actions, 'sms.notify'))}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData((prev) => {
                    const base = stripOutboundPolicyActions(prev.actions);
                    const rest = otherOutboundThan(prev.actions, 'sms.notify');
                    if (!on) {
                      const merged = [...base, ...rest];
                      return { ...prev, actions: merged.length ? merged : undefined } as any;
                    }
                    const existing = getPolicyActionByType(prev.actions, 'sms.notify');
                    const addition = {
                      id: existing?.id || 'sms_notify',
                      type: 'sms.notify',
                      enabled: true,
                      plane: 'cloud',
                      params: {
                        to: String(existing?.params?.to ?? ''),
                        body: String(existing?.params?.body ?? ''),
                      },
                    };
                    return { ...prev, actions: [...base, ...rest, addition] } as any;
                  });
                }}
              />
            }
            label="SMS / Twilio (on incident.created)"
          />
          {getPolicyActionByType((formData as any).actions, 'sms.notify') ? (
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="To (E.164)"
                placeholder="+15551234567"
                value={String(getPolicyActionByType((formData as any).actions, 'sms.notify')?.params?.to ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'sms.notify' ? { ...a, params: { ...a.params, to: v } } : a,
                    ),
                  }));
                }}
                helperText="Optional if cloud_api has SMS_NOTIFY_DEFAULT_TO (lab default)."
              />
              <TextField
                fullWidth
                size="small"
                label="Message (optional)"
                multiline
                minRows={2}
                value={String(getPolicyActionByType((formData as any).actions, 'sms.notify')?.params?.body ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'sms.notify' ? { ...a, params: { ...a.params, body: v } } : a,
                    ),
                  }));
                }}
                helperText="If empty, the server builds a short default from the incident."
              />
            </Box>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(getPolicyActionByType((formData as any).actions, 'genetec.notify'))}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData((prev) => {
                    const base = stripOutboundPolicyActions(prev.actions);
                    const rest = otherOutboundThan(prev.actions, 'genetec.notify');
                    if (!on) {
                      const merged = [...base, ...rest];
                      return { ...prev, actions: merged.length ? merged : undefined } as any;
                    }
                    const existing = getPolicyActionByType(prev.actions, 'genetec.notify');
                    const addition = {
                      id: existing?.id || 'genetec_notify',
                      type: 'genetec.notify',
                      enabled: true,
                      plane: 'cloud',
                      params: {
                        url: String(existing?.params?.url ?? ''),
                        event_type: String(existing?.params?.event_type ?? 'genetec.incident.created'),
                        wrap_envelope: truthyParamFlag(existing?.params?.wrap_envelope),
                      },
                    };
                    return { ...prev, actions: [...base, ...rest, addition] } as any;
                  });
                }}
              />
            }
            label="Genetec HTTP notify (on incident.created)"
          />
          {getPolicyActionByType((formData as any).actions, 'genetec.notify') ? (
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="HTTPS URL"
                placeholder="https://partner.example.com/edgentik/incidents"
                value={String(getPolicyActionByType((formData as any).actions, 'genetec.notify')?.params?.url ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'genetec.notify' ? { ...a, params: { ...a.params, url: v } } : a,
                    ),
                  }));
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="Event type"
                value={String(getPolicyActionByType((formData as any).actions, 'genetec.notify')?.params?.event_type ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'genetec.notify' ? { ...a, params: { ...a.params, event_type: v } } : a,
                    ),
                  }));
                }}
                helperText="Stored on the webhook envelope when wrap is enabled."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={truthyParamFlag(
                      getPolicyActionByType((formData as any).actions, 'genetec.notify')?.params?.wrap_envelope,
                    )}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => ({
                        ...prev,
                        actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                          String(a?.type) === 'genetec.notify'
                            ? { ...a, params: { ...a.params, wrap_envelope: checked } }
                            : a,
                        ),
                      }));
                    }}
                  />
                }
                label="Wrap in standard webhook envelope"
              />
            </Box>
          ) : null}

          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(getPolicyActionByType((formData as any).actions, 'webhook.call'))}
                onChange={(e) => {
                  const on = e.target.checked;
                  setFormData((prev) => {
                    const base = stripOutboundPolicyActions(prev.actions);
                    const rest = otherOutboundThan(prev.actions, 'webhook.call');
                    if (!on) {
                      const merged = [...base, ...rest];
                      return { ...prev, actions: merged.length ? merged : undefined } as any;
                    }
                    const existing = getPolicyActionByType(prev.actions, 'webhook.call');
                    const addition = {
                      id: existing?.id || 'webhook_call',
                      type: 'webhook.call',
                      enabled: true,
                      plane: 'cloud',
                      params: {
                        url: String(existing?.params?.url ?? ''),
                        method: String(existing?.params?.method ?? 'POST'),
                      },
                    };
                    return { ...prev, actions: [...base, ...rest, addition] } as any;
                  });
                }}
              />
            }
            label="Webhook (on incident.created)"
          />
          {getPolicyActionByType((formData as any).actions, 'webhook.call') ? (
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="URL"
                value={String(getPolicyActionByType((formData as any).actions, 'webhook.call')?.params?.url ?? '')}
                onChange={(e) => {
                  const v = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                      String(a?.type) === 'webhook.call' ? { ...a, params: { ...a.params, url: v } } : a,
                    ),
                  }));
                }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Method</InputLabel>
                <Select
                  label="Method"
                  value={String(getPolicyActionByType((formData as any).actions, 'webhook.call')?.params?.method ?? 'POST').toUpperCase()}
                  onChange={(e) => {
                    const v = String(e.target.value || 'POST');
                    setFormData((prev) => ({
                      ...prev,
                      actions: (Array.isArray(prev.actions) ? prev.actions : []).map((a: any) =>
                        String(a?.type) === 'webhook.call' ? { ...a, params: { ...a.params, method: v } } : a,
                      ),
                    }));
                  }}
                >
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="PATCH">PATCH</MenuItem>
                </Select>
              </FormControl>
            </Box>
          ) : null}

          <FormControlLabel
            control={<Checkbox disabled />}
            label="Jira (coming soon — configure JIRA_* on server)"
          />

          <Divider sx={{ my: 2 }} />

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={formData.alert?.severity || 'medium'}
              label="Severity"
              onChange={(e) =>
                setFormData({
                  ...formData,
                  alert: canonicalAlertForSave({ ...formData.alert, severity: e.target.value as any }),
                })
              }
            >
              {SEVERITIES.map((sev) => (
                <MenuItem key={sev.value} value={sev.value}>
                  {sev.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Severity is the operational priority of this policy. It is not the same as detection Sensitivity.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={immediateAlertEnabled(formData.alert)}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    alert: canonicalAlertForSave({
                      ...formData.alert,
                      severity: formData.alert?.severity || 'medium',
                      enabled: e.target.checked,
                    }),
                  })
                }
              />
            }
            label="Immediate operational alert"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Immediate Alert opens when qualifying activity creates a new operational incident.
            Repeated activity is grouped, and notification frequency is limited by the configured
            timing. This is independent of Pattern Alert.
          </Typography>

          <TextField
            fullWidth
            label="Message template (optional)"
            value={formData.alert?.message_template || ''}
            onChange={(e) => setFormData({ ...formData, alert: canonicalAlertForSave({ ...(formData.alert as any), message_template: e.target.value }) })}
            sx={{ mb: 2 }}
            helperText="Example: Unauthorized entry to {zone_name}"
          />

          {(PATTERN_ALERT_POLICY_TYPES as readonly string[]).includes(String(formData.policy_type || '')) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2">Pattern Alert</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Alert when this condition keeps recurring. Create a separate operational incident when this qualified condition recurs N times within a rolling window.
                This does not change immediate alerts, incident grouping, or notification cooldown.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={(formData.pattern as any)?.enabled === true}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setFormData({
                        ...formData,
                        pattern: enabled
                          ? canonicalPatternPayload({
                              enabled: true,
                              minEvents: (formData.pattern as any)?.min_events,
                              windowMinutes: Number((formData.pattern as any)?.window_seconds ?? 3600) / 60,
                              cooldownHours: Number((formData.pattern as any)?.cooldown_seconds ?? 14400) / 3600,
                            })
                          : { enabled: false },
                      });
                    }}
                  />
                }
                label="Alert when this condition keeps recurring"
              />
              {(formData.pattern as any)?.enabled === true && (
                <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                  <TextField
                    label="Occurrences"
                    type="number"
                    inputProps={{ min: 2 }}
                    value={(formData.pattern as any)?.min_events ?? 5}
                    onChange={(e) => setFormData({
                      ...formData,
                      pattern: { ...(formData.pattern as any), min_events: Number(e.target.value) },
                    })}
                    helperText="Minimum 2 qualified occurrences."
                    sx={{ minWidth: 180, flex: 1 }}
                  />
                  <TextField
                    label="Within (minutes)"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={Number((formData.pattern as any)?.window_seconds ?? 3600) / 60}
                    onChange={(e) => setFormData({
                      ...formData,
                      pattern: { ...(formData.pattern as any), window_seconds: Number(e.target.value) * 60 },
                    })}
                    helperText="Rolling event-time window."
                    sx={{ minWidth: 180, flex: 1 }}
                  />
                  <TextField
                    label="Cooldown (hours)"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={Number((formData.pattern as any)?.cooldown_seconds ?? 14400) / 3600}
                    onChange={(e) => setFormData({
                      ...formData,
                      pattern: { ...(formData.pattern as any), cooldown_seconds: Number(e.target.value) * 3600 },
                    })}
                    helperText="Pattern-only suppression after firing."
                    sx={{ minWidth: 180, flex: 1 }}
                  />
                </Box>
              )}
              {(formData.policy_type === 'forklift_proximity' || formData.policy_type === 'pedestrian_near_moving_forklift') && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {FORKLIFT_PATTERN_COMMISSIONING_COPY}
                </Alert>
              )}
            </>
          )}
          <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }} data-testid="effective-alert-readout">
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Effective Alert configuration
            </Typography>
            {effectiveAlertLinesFromForm(formData).map((line) => (
              <Typography key={line} variant="body2">{line}</Typography>
            ))}
          </Paper>
        </>
      )}
    </>
  );
};

export default PolicyConfigureStep;
